import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import BottomNav from '../components/BottomNav';

const CATEGORIES = [
  { value: 'ROBBERY', label: 'Robbery' },
  { value: 'RAM_RAID', label: 'Ram Raid' },
  { value: 'BURGLARY', label: 'Burglary' },
  { value: 'ASSAULT', label: 'Assault' },
  { value: 'FLY_TIPPING', label: 'Fly Tipping' },
  { value: 'HOSTILE_RECON', label: 'Hostile Recon' },
  { value: 'SUSPECTED_DRUG_ACTIVITY', label: 'Suspected Drug Activity' },
  { value: 'ANTI_SOCIAL_BEHAVIOUR', label: 'Anti-Social Behaviour (ASB)' },
  { value: 'CRIMINAL_DAMAGE', label: 'Criminal Damage' },
  { value: 'THEFT_OF_FROM_VEHICLE', label: 'Theft of or from Vehicle' },
  { value: 'DANGEROUS_DRIVING', label: 'Dangerous Driving' },
  { value: 'VEHICLE_CAUSING_OBSTRUCTION', label: 'Vehicle Causing Obstruction' },
  { value: 'CLONED_PLATES', label: 'Cloned Plates' },
  { value: 'OTHER', label: 'Other' },
];

const EVIDENCE_SOURCES = [
  { value: 'OBSERVED', label: 'Observed' },
  { value: 'CCTV', label: 'Monitored on CCTV' },
];

// §1: Narrative only accepted for these categories
const NARRATIVE_REQUIRED_CATEGORIES = ['ROBBERY', 'RAM_RAID', 'BURGLARY'];

const UK_POLICE_FORCES = [
  'Avon and Somerset Police','Bedfordshire Police','Cambridgeshire Constabulary',
  'Cheshire Constabulary','City of London Police','Cleveland Police',
  'Cumbria Constabulary','Derbyshire Constabulary','Devon and Cornwall Police',
  'Dorset Police','Durham Constabulary','Essex Police',
  'Gloucestershire Constabulary','Greater Manchester Police','Hampshire Constabulary',
  'Hertfordshire Constabulary','Humberside Police','Kent Police',
  'Lancashire Constabulary','Leicestershire Police','Lincolnshire Police',
  'Merseyside Police','Metropolitan Police Service','Norfolk Constabulary',
  'North Yorkshire Police','Northamptonshire Police','Northumbria Police',
  'Nottinghamshire Police','South Yorkshire Police','Staffordshire Police',
  'Suffolk Constabulary','Surrey Police','Sussex Police',
  'Thames Valley Police','Warwickshire Police','West Mercia Police',
  'West Midlands Police','West Yorkshire Police','Wiltshire Police',
  'Dyfed-Powys Police','Gwent Police','North Wales Police','South Wales Police',
  'Police Scotland','Police Service of Northern Ireland',
  'British Transport Police','Ministry of Defence Police','Civil Nuclear Constabulary',
];

// Shared input style
const inputStyle = {
  width: '100%', height: '48px', padding: '0 14px',
  background: 'white', border: '1.5px solid #e4e8f0',
  borderRadius: '10px', fontSize: '14px', color: '#001a4d',
  fontFamily: "'Inter', sans-serif", boxSizing: 'border-box',
  outline: 'none',
};

const inputErrorStyle = { ...inputStyle, border: '1.5px solid #dc2626' };

const selectStyle = { ...inputStyle, appearance: 'none', cursor: 'pointer' };

const labelStyle = {
  display: 'block', fontFamily: "'Oswald', sans-serif",
  fontSize: '10px', fontWeight: 600, letterSpacing: '2px',
  textTransform: 'uppercase', color: '#4a5a7a', marginBottom: '8px',
};

const cardStyle = {
  background: 'white', borderRadius: '12px', padding: '16px',
  marginBottom: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
};

const errorText = { fontSize: '11px', color: '#dc2626', marginTop: '4px' };

function SectionCard({ children }) {
  return <div style={cardStyle}>{children}</div>;
}

function ThankYouModal({ onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 50,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px', background: 'rgba(0,0,0,0.6)',
    }}>
      <div style={{
        background: 'white', borderRadius: '16px', padding: '32px',
        maxWidth: '340px', width: '100%', textAlign: 'center',
        boxShadow: '0 24px 48px rgba(0,0,0,0.3)',
      }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: '50%',
          background: '#f0fdf4', display: 'flex', alignItems: 'center',
          justifyContent: 'center', margin: '0 auto 20px',
        }}>
          <CheckCircle size={32} color="#15803d" />
        </div>
        <div style={{
          fontFamily: "'Oswald', sans-serif", fontSize: '20px',
          fontWeight: 700, color: '#001a4d', marginBottom: '12px',
          letterSpacing: '1px',
        }}>Sighting Recorded</div>
        <p style={{ fontSize: '13px', color: '#7a8aaa', lineHeight: 1.6, marginBottom: '24px' }}>
          Thank you for your contribution to the Vehicle Crime Intelligence Network. Your sighting will help keep communities safer.
        </p>
        <button onClick={onClose} style={{
          width: '100%', padding: '14px', background: '#001a4d',
          border: 'none', borderRadius: '10px', color: 'white',
          fontFamily: "'Oswald', sans-serif", fontSize: '15px',
          fontWeight: 600, letterSpacing: '3px', textTransform: 'uppercase',
          cursor: 'pointer',
        }} data-testid="thank-you-close-btn">Return to Home</button>
      </div>
    </div>
  );
}

export default function SightingFormScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const prefilledVRN = location.state?.vrn || '';
  const isVRNLocked = !!prefilledVRN;

  const [vrn, setVrn] = useState(prefilledVRN);
  const [vehicleMake, setVehicleMake] = useState('');
  const [vehicleModel, setVehicleModel] = useState('');
  const [vehicleColour, setVehicleColour] = useState('');
  const [siteId, setSiteId] = useState('');
  const [sightingDate, setSightingDate] = useState(formatDateOnly(new Date()));
  const [sightingTime, setSightingTime] = useState(formatTimeOnly(new Date()));
  const [category, setCategory] = useState('');
  const [otherCategoryText, setOtherCategoryText] = useState('');
  const [evidenceSource, setEvidenceSource] = useState('');
  const [briefDescription, setBriefDescription] = useState('');
  const [reportedToPolice, setReportedToPolice] = useState(null);
  const [reportedToForce, setReportedToForce] = useState('');
  const [policeIncidentNumber, setPoliceIncidentNumber] = useState('');
  const [locationText, setLocationText] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sites, setSites] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [showThankYou, setShowThankYou] = useState(false);

  function formatDateOnly(date) {
    return new Date(date).toISOString().slice(0, 10);
  }
  function formatTimeOnly(date) {
    return new Date(date).toTimeString().slice(0, 5);
  }
  function getTodayDate() {
    return new Date().toISOString().slice(0, 10);
  }

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await api.get('/auth/me');
        const assignedSites = response.data.assigned_sites || [];
        if (assignedSites.length > 0) {
          const formattedSites = assignedSites.map(site => ({
            _id: site.id || site,
            name: site.name || 'Unknown Site',
          }));
          setSites(formattedSites);
          if (formattedSites.length === 1) setSiteId(formattedSites[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch sites:', err);
      }
    };
    fetchSites();
  }, []);

  const handleVRNChange = (e) => {
    if (isVRNLocked) return;
    const value = e.target.value.toUpperCase().replace(/\s/g, '');
    setVrn(value);
    if (validationErrors.vrn) setValidationErrors(prev => ({ ...prev, vrn: null }));
  };

  const validateForm = () => {
    const errors = {};
    if (!vrn.trim()) errors.vrn = 'VRN is required';
    if (!siteId) errors.siteId = 'Please select a site';
    if (!sightingDate) errors.sightingDate = 'Date is required';
    if (!sightingTime) errors.sightingTime = 'Time is required';
    if (sightingDate && sightingTime) {
      if (new Date(`${sightingDate}T${sightingTime}`) > new Date()) {
        errors.sightingDate = 'Date/time cannot be in the future';
      }
    }
    if (!category) errors.category = 'Please select what you observed';
    if (category === 'OTHER' && !otherCategoryText.trim()) errors.otherCategoryText = 'Please specify the incident type';
    if (!evidenceSource) errors.evidenceSource = 'Please select evidence source';
    if (reportedToPolice === null) errors.reportedToPolice = 'Please indicate if reporting to police';
    if (reportedToPolice === true && !reportedToForce) errors.reportedToForce = 'Please select the police force';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isFormComplete = () => {
    if (!vrn.trim() || !siteId || !sightingDate || !sightingTime || !category || !evidenceSource) return false;
    if (category === 'OTHER' && !otherCategoryText.trim()) return false;
    if (reportedToPolice === null) return false;
    if (reportedToPolice === true && !reportedToForce) return false;
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError(null);
    try {
      const combinedDatetime = new Date(`${sightingDate}T${sightingTime}`).toISOString();
      const payload = {
        vrn, site_id: siteId, datetime: combinedDatetime,
        primary_category: category, confidence_level: evidenceSource,
        reported_to_police: reportedToPolice,
      };
      // §1: Only send narrative for qualifying categories
      if (NARRATIVE_REQUIRED_CATEGORIES.includes(category) && briefDescription.trim()) {
        payload.narrative = briefDescription.trim();
      }
      if (reportedToForce) payload.reported_to_force = reportedToForce;
      if (policeIncidentNumber.trim()) payload.police_incident_number = policeIncidentNumber.trim();
      if (locationText.trim()) payload.location_text = locationText.trim();
      if (vehicleMake.trim()) payload.vehicle_make = vehicleMake.trim();
      if (vehicleModel.trim()) payload.vehicle_model = vehicleModel.trim();
      if (vehicleColour.trim()) payload.vehicle_colour = vehicleColour.trim();

      await api.post('/sightings', payload);
      setShowThankYou(true);
    } catch (err) {
      setError(err.message || 'Failed to submit sighting. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleThankYouClose = () => {
    setShowThankYou(false);
    navigate('/', { replace: true });
  };

  const toggleBtn = (active) => ({
    flex: 1, height: '48px', border: 'none', borderRadius: '10px',
    fontFamily: "'Oswald', sans-serif", fontSize: '13px', fontWeight: 600,
    letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer',
    background: active ? '#001a4d' : '#f0f2f7',
    color: active ? 'white' : '#7a8aaa',
    transition: 'all 0.15s',
  });

  return (
    <div style={{
      minHeight: '100vh', background: '#001a4d',
      fontFamily: "'Inter', sans-serif", display: 'flex', flexDirection: 'column',
    }} data-testid="sighting-form-screen">

      {showThankYou && <ThankYouModal onClose={handleThankYouClose} />}

      {/* Header */}
      <div style={{
        background: '#001a4d', padding: '48px 20px 20px',
        borderBottom: '3px solid #0a2a6e', position: 'relative',
        display: 'flex', alignItems: 'center', gap: '12px',
      }}>
        <button onClick={() => navigate(-1)} style={{
          width: '38px', height: '38px', background: 'rgba(255,255,255,0.1)',
          border: 'none', borderRadius: '10px', display: 'flex',
          alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
        }} data-testid="back-btn">
          <ArrowLeft size={18} color="white" />
        </button>
        <div>
          <div style={{
            fontFamily: "'Oswald', sans-serif", fontSize: '20px',
            fontWeight: 700, color: 'white', letterSpacing: '2px',
            textTransform: 'uppercase',
          }}>Log a Sighting</div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
            Submit vehicle intelligence
          </div>
        </div>
      </div>

      {/* Form */}
      <div style={{ flex: 1, padding: '16px', overflowY: 'auto', paddingBottom: '120px' }}>

        {error && (
          <div style={{
            background: '#fff5f5', border: '1.5px solid #fecaca',
            borderLeft: '4px solid #dc2626', borderRadius: '10px',
            padding: '12px 14px', marginBottom: '12px',
            display: 'flex', alignItems: 'flex-start', gap: '10px',
          }} data-testid="form-error">
            <AlertCircle size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: '1px' }} />
            <span style={{ fontSize: '13px', color: '#dc2626' }}>{error}</span>
          </div>
        )}

        {/* VRN */}
        <SectionCard>
          <div style={labelStyle}>Vehicle Registration</div>
          <input
            type="text"
            value={vrn}
            onChange={handleVRNChange}
            disabled={isVRNLocked}
            placeholder="AB12 CDE"
            style={{
              width: '100%', height: '56px', textAlign: 'center',
              fontSize: '26px', fontWeight: 700, letterSpacing: '8px',
              fontFamily: "'Oswald', sans-serif",
              background: isVRNLocked ? '#e4e8f0' : '#F5C400',
              border: `3px solid ${validationErrors.vrn ? '#dc2626' : '#111'}`,
              borderRadius: '8px', color: '#111', outline: 'none',
              cursor: isVRNLocked ? 'not-allowed' : 'text',
              boxSizing: 'border-box',
            }}
            data-testid="vrn-input"
          />
          {validationErrors.vrn && <p style={errorText}>{validationErrors.vrn}</p>}
        </SectionCard>

        {/* Vehicle Description */}
        <SectionCard>
          <div style={labelStyle}>Vehicle Description <span style={{ color: '#b0bcd4', fontWeight: 400, letterSpacing: 0, textTransform: 'none', fontSize: '10px' }}>(optional)</span></div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
            <div style={{ flex: 1 }}>
              <input type="text" value={vehicleMake} onChange={e => setVehicleMake(e.target.value)}
                placeholder="Make" style={inputStyle} data-testid="vehicle-make-input" />
            </div>
            <div style={{ flex: 1 }}>
              <input type="text" value={vehicleModel} onChange={e => setVehicleModel(e.target.value)}
                placeholder="Model" style={inputStyle} data-testid="vehicle-model-input" />
            </div>
          </div>
          <input type="text" value={vehicleColour} onChange={e => setVehicleColour(e.target.value)}
            placeholder="Colour  e.g. Dark Blue, Metallic Grey" style={inputStyle} data-testid="vehicle-colour-input" />
        </SectionCard>

        {/* Site */}
        <SectionCard>
          <div style={labelStyle}>Which site are you at?</div>
          {sites.length === 0 ? (
            <div style={{
              padding: '12px 14px', background: '#fffbf0',
              border: '1px solid #f0d890', borderLeft: '3px solid #C9973A',
              borderRadius: '8px', fontSize: '12px', color: '#92400E',
            }} data-testid="no-sites-message">
              ⚠️ No sites assigned — contact your administrator.
            </div>
          ) : (
            <select value={siteId} onChange={e => {
              setSiteId(e.target.value);
              if (validationErrors.siteId) setValidationErrors(prev => ({ ...prev, siteId: null }));
            }} style={validationErrors.siteId ? { ...selectStyle, border: '1.5px solid #dc2626' } : selectStyle}
              data-testid="site-select">
              <option value="">Select a site...</option>
              {sites.map(site => <option key={site.id} value={site.id}>{site.name}</option>)}
            </select>
          )}
          {validationErrors.siteId && sites.length > 0 && <p style={errorText}>{validationErrors.siteId}</p>}
        </SectionCard>

        {/* Date & Time */}
        <SectionCard>
          <div style={labelStyle}>When did you see this vehicle?</div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input type="date" value={sightingDate} max={getTodayDate()}
              onChange={e => {
                setSightingDate(e.target.value);
                if (validationErrors.sightingDate) setValidationErrors(prev => ({ ...prev, sightingDate: null }));
              }}
              style={{ ...(validationErrors.sightingDate ? inputErrorStyle : inputStyle), width: '50%' }}
              data-testid="date-input" />
            <input type="time" value={sightingTime}
              onChange={e => {
                setSightingTime(e.target.value);
                if (validationErrors.sightingTime) setValidationErrors(prev => ({ ...prev, sightingTime: null }));
              }}
              style={{ ...(validationErrors.sightingTime ? inputErrorStyle : inputStyle), width: '50%' }}
              data-testid="time-input" />
          </div>
          {(validationErrors.sightingDate || validationErrors.sightingTime) && (
            <p style={errorText}>{validationErrors.sightingDate || validationErrors.sightingTime}</p>
          )}
        </SectionCard>

        {/* Category */}
        <SectionCard>
          <div style={labelStyle}>What did you observe?</div>
          <select value={category} onChange={e => {
            setCategory(e.target.value);
            if (e.target.value !== 'OTHER') setOtherCategoryText('');
            if (validationErrors.category) setValidationErrors(prev => ({ ...prev, category: null }));
          }} style={validationErrors.category ? { ...selectStyle, border: '1.5px solid #dc2626' } : selectStyle}
            data-testid="category-select">
            <option value="">Select observation type...</option>
            {CATEGORIES.map(cat => <option key={cat.value} value={cat.value}>{cat.label}</option>)}
          </select>
          {validationErrors.category && <p style={errorText}>{validationErrors.category}</p>}

          {category === 'OTHER' && (
            <div style={{ marginTop: '10px' }}>
              <input type="text" value={otherCategoryText}
                onChange={e => {
                  setOtherCategoryText(e.target.value);
                  if (validationErrors.otherCategoryText) setValidationErrors(prev => ({ ...prev, otherCategoryText: null }));
                }}
                placeholder="Describe the incident type..."
                style={validationErrors.otherCategoryText ? inputErrorStyle : inputStyle}
                data-testid="other-category-input" />
              {validationErrors.otherCategoryText && <p style={errorText}>{validationErrors.otherCategoryText}</p>}
            </div>
          )}

          <div style={{
            marginTop: '10px', padding: '10px 12px',
            background: '#fffbf0', border: '1px solid #f0d890',
            borderLeft: '3px solid #C9973A', borderRadius: '8px',
            fontSize: '11px', color: '#92400E', lineHeight: 1.5,
          }}>
            ⚠️ All observations are suspected activity only and do not constitute confirmed criminal offences.
          </div>
        </SectionCard>

        {/* §7: Transparency disclaimer */}
        <SectionCard>
          <div style={{
            fontSize: '11px', color: '#6b7a94', lineHeight: 1.5,
            background: '#f8f9fc', borderRadius: '8px', padding: '10px 12px',
            border: '1px solid #e4e8f0',
          }}>
            This record may constitute personal data about the registered keeper.
            Submit only what you have lawful basis to record.
            {' '}<a href="/privacy" style={{ color: '#001a4d' }}>Privacy Notice</a>
          </div>
        </SectionCard>

        {/* Brief Description — §1: only shown for ROBBERY, RAM_RAID, BURGLARY */}
        {NARRATIVE_REQUIRED_CATEGORIES.includes(category) && (
          <SectionCard>
            <div style={labelStyle}>Brief Description <span style={{ color: '#dc2626', fontWeight: 400, letterSpacing: 0, textTransform: 'none', fontSize: '10px' }}>(required)</span></div>
            <textarea value={briefDescription} onChange={e => setBriefDescription(e.target.value)}
              placeholder="Describe what you observed — required for this category..."
              rows={3} style={{
                ...inputStyle, height: 'auto', padding: '12px 14px',
                resize: 'none', lineHeight: 1.5,
              }}
              data-testid="brief-description-input" />
          </SectionCard>
        )}

        {/* Evidence Source */}
        <SectionCard>
          <div style={labelStyle}>Evidence Source</div>
          <div style={{ display: 'flex', gap: '10px' }} data-testid="evidence-source">
            {EVIDENCE_SOURCES.map(source => (
              <button key={source.value} type="button"
                onClick={() => {
                  setEvidenceSource(source.value);
                  if (validationErrors.evidenceSource) setValidationErrors(prev => ({ ...prev, evidenceSource: null }));
                }}
                style={toggleBtn(evidenceSource === source.value)}
                data-testid={`evidence-${source.value}`}>
                {source.label}
              </button>
            ))}
          </div>
          {validationErrors.evidenceSource && <p style={errorText}>{validationErrors.evidenceSource}</p>}
        </SectionCard>

        {/* Police Reporting */}
        <SectionCard>
          <div style={labelStyle}>Reporting to police?</div>
          <div style={{ display: 'flex', gap: '10px' }} data-testid="police-reported">
            <button type="button" onClick={() => {
              setReportedToPolice(true);
              if (validationErrors.reportedToPolice) setValidationErrors(prev => ({ ...prev, reportedToPolice: null }));
            }} style={toggleBtn(reportedToPolice === true)} data-testid="police-yes">Yes</button>
            <button type="button" onClick={() => {
              setReportedToPolice(false);
              setReportedToForce(''); setPoliceIncidentNumber('');
              if (validationErrors.reportedToPolice) setValidationErrors(prev => ({ ...prev, reportedToPolice: null }));
              if (validationErrors.reportedToForce) setValidationErrors(prev => ({ ...prev, reportedToForce: null }));
            }} style={toggleBtn(reportedToPolice === false)} data-testid="police-no">No</button>
          </div>
          {validationErrors.reportedToPolice && <p style={errorText}>{validationErrors.reportedToPolice}</p>}

          {reportedToPolice === true && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ ...labelStyle, marginTop: '4px' }}>Which police force?</div>
              <select value={reportedToForce} onChange={e => {
                setReportedToForce(e.target.value);
                if (validationErrors.reportedToForce) setValidationErrors(prev => ({ ...prev, reportedToForce: null }));
              }} style={validationErrors.reportedToForce ? { ...selectStyle, border: '1.5px solid #dc2626' } : selectStyle}
                data-testid="police-force-select">
                <option value="">Select police force...</option>
                {UK_POLICE_FORCES.map(force => <option key={force} value={force}>{force}</option>)}
              </select>
              {validationErrors.reportedToForce && <p style={errorText}>{validationErrors.reportedToForce}</p>}

              <div style={{ ...labelStyle, marginTop: '12px' }}>Incident Number <span style={{ color: '#b0bcd4', fontWeight: 400, letterSpacing: 0, textTransform: 'none', fontSize: '10px' }}>(optional)</span></div>
              <input type="text" value={policeIncidentNumber}
                onChange={e => setPoliceIncidentNumber(e.target.value)}
                placeholder="e.g. CAD-123456" style={inputStyle}
                data-testid="police-number-input" />
              <div style={{
                marginTop: '10px', padding: '10px 12px',
                background: '#fffbf0', border: '1px solid #f0d890',
                borderLeft: '3px solid #C9973A', borderRadius: '8px',
                fontSize: '11px', color: '#92400E', lineHeight: 1.5,
              }}>
                ⚠️ Ask the officer for an incident number before submitting.
              </div>
            </div>
          )}
        </SectionCard>

        {/* Location Notes */}
        <SectionCard>
          <div style={labelStyle}>Additional Location Details <span style={{ color: '#b0bcd4', fontWeight: 400, letterSpacing: 0, textTransform: 'none', fontSize: '10px' }}>(optional)</span></div>
          <input type="text" value={locationText} onChange={e => setLocationText(e.target.value)}
            placeholder="e.g. North car park, near entrance" style={inputStyle}
            data-testid="location-input" />
        </SectionCard>

        {/* Submit */}
        <button type="button" onClick={handleSubmit}
          disabled={loading || !isFormComplete()}
          style={{
            width: '100%', padding: '16px', border: 'none', borderRadius: '10px',
            background: isFormComplete() && !loading ? 'white' : 'rgba(255,255,255,0.25)',
            color: isFormComplete() && !loading ? '#001a4d' : 'rgba(255,255,255,0.5)',
            fontFamily: "'Oswald', sans-serif", fontSize: '17px', fontWeight: 700,
            letterSpacing: '4px', textTransform: 'uppercase', cursor: isFormComplete() && !loading ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            boxShadow: isFormComplete() && !loading ? '0 4px 16px rgba(0,0,0,0.3)' : 'none',
            transition: 'all 0.2s', marginBottom: '8px',
          }}
          data-testid="submit-btn">
          {loading ? <><Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> Submitting...</> : 'Submit Sighting'}
        </button>

      </div>

      <BottomNav activeTab="log" />
    </div>
  );
}
